import { SubstepType } from "~/features/configurator/Linear/dtos";
import {
  makeLabelGetter,
  makeNumberGetter,
  prepareSubStepsInfo,
  type SummaryData,
  type SummaryDataProvider,
} from "~/features/configurator/Shaft/SummaryDataProvider";
import type { LinearCstic } from "~/features/configurator/Linear/models";
import { InputCstics } from "~/features/configurator/Linear/components/Substeps/GuidewaySetStep/constants";
import { getMainKmatCstics } from "~/features/configurator/common/v2/helper";
import { getMainKmat } from "~/features/configurator/Shaft/common/commonShaftHelpers";
import { removeMultipleTags } from "~/common/utils/domUtils";
import * as UnitCstics from "../KUEMUnitCstics";
import { isNonNull } from "~/common/utils/reducers";

export function getStep1SummaryData(summaryProvider: SummaryDataProvider): SummaryData[] | null {
  const substeps = [
    SubstepType.SystemLayout,
    SubstepType.ReferenceCarriage,
    SubstepType.Size,
    SubstepType.Preload,
    SubstepType.Accuracy,
    SubstepType.Sealing,
  ];
  return [...(prepareSubStepsInfo(summaryProvider, substeps) ?? []), ...getSubstep7SummaryData(summaryProvider)];
}

export function getSubstep7SummaryData(summaryProvider: SummaryDataProvider): SummaryData[] {
  const substeps = [SubstepType.LocatingFace];
  const t = summaryProvider.t;

  const summaryData = prepareSubStepsInfo(summaryProvider, substeps) ?? [];

  [UnitCstics.Closure]
    .filter(isNonNull)
    .map((name) => {
      const cstics = getMainKmat({ kmats: summaryProvider.kmats }).cstics;
      return cstics[name];
    })
    .filter((cstic) => cstic?.value)
    .forEach((cstic) => {
      const value = cstic.values.find((v) => v.value === cstic.value);
      summaryData.push({
        name: t(cstic.label),
        isStandard: value?.standard ?? false,
        value: t(value?.label ?? ""),
      });
    });

  const configureRailSubstep = summaryProvider.substeps[SubstepType.KLLTGuidewaySet];
  if (configureRailSubstep) {
    const inputCsticNames = [
      InputCstics.TotalLength,
      InputCstics.aL,
      InputCstics.aR,
      // InputCstics.partSizingType,
      InputCstics.partAmount,
    ];

    const inputCsticNamesFormatting: Record<string, (cstic: LinearCstic) => string> = {
      [InputCstics.TotalLength]: makeNumberGetter("mm"),
      [InputCstics.aL]: makeNumberGetter("mm"),
      [InputCstics.aR]: makeNumberGetter("mm"),
      [InputCstics.partSizingType]: makeLabelGetter(t),
      [InputCstics.partAmount]: makeLabelGetter(t),
    };

    const inputCstics = getMainKmatCstics(configureRailSubstep).input;
    const cstics = getMainKmat({ kmats: summaryProvider.kmats }).cstics;

    inputCsticNames.forEach((inputCsticName) => {
      const def = inputCstics[inputCsticName];
      const cstic = cstics[def.name];
      const getValue = inputCsticNamesFormatting[inputCsticName];

      if (!cstic || !def.visible) {
        return;
      }

      summaryData.push({
        name: t(cstic.label),
        isStandard: false,
        value: getValue(cstic),
      });
    });
  }

  summaryData.forEach((data) => {
    data.value = removeMultipleTags(data.value, "p", "span")!;
    data.name = removeMultipleTags(data.name, "p", "span")!;
  });

  return summaryData;
}

function getIsStandard(cstic: LinearCstic) {
  const cValue = cstic.values.find((cVal) => cVal.value === cstic.value);
  return cValue?.standard ?? false;
}
